import { useContext, useMemo } from "react";
import {
    Box,
    Typography,
    FormGroup,
    FormControl,
    Select,
    MenuItem,
    TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { defaultPandFOptions, PandFSeriesOptions } from "../chartV2/customSeries/p&FSeries/options";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import CommonChartSettingsTabs from './commonChartSettingsTabs';
import {
    chartSettingsColorRowLabelSx,
    chartSettingsFormGroupSx,
    chartSettingsGridRowSx,
    chartSettingsLabelSx,
    chartSettingsRowControlsSx,
    chartSettingsSectionDividerSx,
    chartSettingsSectionTitleSx,
} from './chartSettingsLayout';

type Props = {
    viewKey: string;
};

const PandFSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))

    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    const boxMethodSettings = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as PandFSeriesOptions)?.boxMethod ?? defaultPandFOptions.boxMethod
        }
        return defaultPandFOptions.boxMethod
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const upColor = useMemo(() => {
        let color = defaultPandFOptions.upColor;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as PandFSeriesOptions)?.upColor ?? defaultPandFOptions.upColor
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const downColor = useMemo(() => {
        let color = defaultPandFOptions.downColor;
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as PandFSeriesOptions)?.downColor ?? defaultPandFOptions.downColor
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof PandFSeriesOptions) => {
        const series = chart?.primarySeriesGroup?.getPrimarySeries()
        if (series && 'updateOptions' in series) {
            series.updateOptions({
                [type]: newColor
            } as any);
        }
    }

    const handleBoxMethodChange = (method: 'atr' | 'traditional' | 'percentage') => {
        const series = chart?.primarySeriesGroup?.getPrimarySeries()
        if (series && 'updateOptions' in series) {
            series.updateOptions({
                boxMethod: method
            } as any)
        }
    }

    const boxSizeValue = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as PandFSeriesOptions
            if (boxMethodSettings === 'atr') return series.atrLength ?? defaultPandFOptions.atrLength
            if (boxMethodSettings === 'percentage') return series.percentage ?? defaultPandFOptions.percentage
            return series.boxSize ?? defaultPandFOptions.boxSize
        }
        if (boxMethodSettings === 'atr') return defaultPandFOptions.atrLength
        if (boxMethodSettings === 'percentage') return defaultPandFOptions.percentage
        return defaultPandFOptions.boxSize
    }, [boxMethodSettings, chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const reversalAmount = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options as PandFSeriesOptions
            return series.reversalAmount ?? defaultPandFOptions.reversalAmount
        }
        return defaultPandFOptions.reversalAmount
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const handleBoxSizeValueChange = (value: number) => {
        if (value > 0) {
            const series = chart?.primarySeriesGroup?.getPrimarySeries()
            if (series && 'updateOptions' in series) {
                const key = boxMethodSettings === 'atr' ? 'atrLength' :
                           boxMethodSettings === 'percentage' ? 'percentage' : 'boxSize'
                series.updateOptions({
                    [key]: value
                } as any)
            }
        }
    }

    const handleReversalAmountChange = (value: number) => {
        if (value > 0) {
            const series = chart?.primarySeriesGroup?.getPrimarySeries()
            if (series && 'updateOptions' in series) {
                series.updateOptions({
                    reversalAmount: value
                } as any)
            }
        }
    }

    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                POINT & FIGURE
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Up (X)</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={upColor.toHexString()}
                            opacity={upColor.getAlpha()}
                            onColorChange={(color, transparency) => {
                                const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                                handleColorChangeSeries(newColor, 'upColor');
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsColorRowLabelSx}>Down (O)</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <ColorSelector
                            compact
                            showOnlySwatch
                            color={downColor.toHexString()}
                            opacity={downColor.getAlpha()}
                            onColorChange={(color, transparency) => {
                                const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                                handleColorChangeSeries(newColor, 'downColor');
                            }}
                        />
                    </Box>
                </Box>
            </FormGroup>
            <Typography variant="subtitle1" sx={{ ...chartSettingsSectionTitleSx, ...chartSettingsSectionDividerSx }}>
                BOX SIZE
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsLabelSx}>Method</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <FormControl size="small">
                            <Select
                                value={boxMethodSettings}
                                onChange={(event) => handleBoxMethodChange(event.target.value as 'atr' | 'traditional' | 'percentage')}
                                sx={{ fontSize: "13px", height: 32, minWidth: 140 }}
                            >
                                <MenuItem value="atr">ATR</MenuItem>
                                <MenuItem value="traditional">Traditional</MenuItem>
                                <MenuItem value="percentage">Percentage LTP</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsLabelSx}>
                        {boxMethodSettings === 'atr'
                            ? 'ATR Length'
                            : boxMethodSettings === 'percentage'
                                ? 'Percentage'
                                : 'Box Size'}
                    </Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <FormControl size="small" sx={{ width: 83 }}>
                            <TextField
                                fullWidth={false}
                                variant="outlined"
                                size="small"
                                value={boxSizeValue}
                                onChange={(event) => handleBoxSizeValueChange(Number(event.target.value))}
                                type="number"
                            />
                        </FormControl>
                    </Box>
                </Box>
                <Box sx={chartSettingsGridRowSx}>
                    <Typography sx={chartSettingsLabelSx}>Reversal</Typography>
                    <Box sx={chartSettingsRowControlsSx}>
                        <FormControl size="small" sx={{ width: 83 }}>
                            <TextField
                                fullWidth={false}
                                variant="outlined"
                                size="small"
                                value={reversalAmount}
                                onChange={(event) => handleReversalAmountChange(Number(event.target.value))}
                                type="number"
                            />
                        </FormControl>
                    </Box>
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default PandFSettings;
